import React, { useEffect, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';

import style from './HoverDropdown.module.scss';

export interface HoverDropdownItem {
    key: string;
    content: React.ReactNode;
    onClick: () => void;
}

interface HoverDropdownProps {
    items: HoverDropdownItem[];
    children: React.ReactNode;
    className?: string;
    // Меню в портале: его не обрежет предок с overflow (например, прокручиваемая таблица).
    inPortal?: boolean;
    // Выравнивание меню в портале относительно триггера: end — меню раскрывается влево от его правого края.
    align?: 'start' | 'end';
}

// Задержка закрытия, чтобы курсор успел перейти с триггера на меню в портале.
const PORTAL_CLOSE_DELAY_MS = 100;
// Отступ меню в портале от края экрана.
const PORTAL_COLLISION_PADDING_PX = 16;

export const HoverDropdown = ({ items, children, className, inPortal = false, align }: HoverDropdownProps) => {
    const [isHovered, setIsHovered] = useState(false);

    if (inPortal) {
        return (
            <PortalHoverDropdown
                items={items}
                className={className}
                align={align}
            >
                {children}
            </PortalHoverDropdown>
        );
    }

    return (
        <div
            className={style.dropdown}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className={className}>{children}</div>
            {isHovered && (
                <div className={style.menu}>
                    <MenuList items={items} />
                </div>
            )}
        </div>
    );
};

function PortalHoverDropdown({ items, children, className, align = 'start' }: Omit<HoverDropdownProps, 'inPortal'>) {
    const [isOpen, setIsOpen] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

    const open = (): void => {
        clearTimeout(closeTimer.current);
        setIsOpen(true);
    };

    const close = (): void => {
        closeTimer.current = setTimeout(() => setIsOpen(false), PORTAL_CLOSE_DELAY_MS);
    };

    useEffect(() => () => clearTimeout(closeTimer.current), []);

    return (
        <Popover.Root open={isOpen}>
            <Popover.Anchor asChild>
                <div
                    className={style.dropdown}
                    onMouseEnter={open}
                    onMouseLeave={close}
                >
                    <div className={className}>{children}</div>
                </div>
            </Popover.Anchor>
            <Popover.Portal>
                <Popover.Content
                    className={style.menu_inPortal}
                    side={'bottom'}
                    align={align}
                    collisionPadding={PORTAL_COLLISION_PADDING_PX}
                    onMouseEnter={open}
                    onMouseLeave={close}
                    onOpenAutoFocus={event => event.preventDefault()}
                >
                    <MenuList items={items} />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}

function MenuList({ items }: Pick<HoverDropdownProps, 'items'>) {
    return (
        <div className={style.menuList}>
            {items.map(item => (
                <div
                    className={style.menuItem}
                    key={item.key}
                    onClick={item.onClick}
                >
                    {item.content}
                </div>
            ))}
        </div>
    );
}
